import { Row, Col, Card, CardBody } from "reactstrap"
import "./FuelPurchaseSummary.css"
import { useState } from "react"

// Helper function to format date
const formatDate = dateStr => {
  if (!dateStr) return "-"
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

// Reusable Stat Card with modern minimal design
const StatCard = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = "primary",
}) => (
  <Card className="border-0 rounded-3 shadow-sm hover-lift transition-all mb-0">
    <CardBody className="p-3">
      <div className="d-flex align-items-center justify-content-between">
        <div className="flex-grow-1">
          <div className="text-muted small fw-medium mb-1">{title}</div>
          <div className="d-flex align-items-baseline gap-2">
            <h4 className="mb-0 fw-bold">{value}</h4>
            {trend && (
              <span
                className={`badge bg-${trend.color}-subtle text-${trend.color} small`}
                title={trend.tooltip}
              >
                {trend.value}
              </span>
            )}
          </div>
          {subtitle && <div className="text-muted small mt-1">{subtitle}</div>}
        </div>
        <div
          style={{ width: "2rem", height: "2rem" }}
          className={`d-flex align-items-center justify-content-center 
            rounded-circle p-2 bg-${color}-subtle`}
        >
          <i className={`bx ${icon} text-${color} fs-5`}></i>
        </div>
      </div>
    </CardBody>
  </Card>
)

// Compact value display component
const ValueItem = ({ icon, label, value, className = "", tooltip }) => (
  <div
    className={`d-flex align-items-center gap-2 small ${className}`}
    title={tooltip}
  >
    <i className={`bx ${icon} text-muted`}></i>
    <span className="text-muted">{label}:</span>
    <span className="fw-medium">{value}</span>
  </div>
)

// Totals component - more compact
const TotalStats = ({ totals = {} }) => {
  const totalFuelQty = parseFloat(totals.total_fuel_qty ?? 0)
  const totalFuelPrice = parseFloat(totals.total_fuel_price ?? 0)
  const avgPricePerLiter = parseFloat(totals.avg_price_per_liter ?? 0)
  const totalFuelUsage = parseFloat(totals.total_fuel_usage ?? 0)
  const remainingFuelUsage = parseFloat(totals.remaining_fuel_usage ?? 0)

  const stats = [
    {
      title: "Total Fuel Stock",
      value: `${totalFuelQty.toLocaleString()} L`,
      subtitle: `${totalFuelPrice.toLocaleString()} BDT total value`,
      icon: "bx-cylinder",
      color: "info",
    },
    {
      title: "Fuel Used",
      value: `${totalFuelUsage.toLocaleString()} L`,
      subtitle: `Consumed fuel from total stock`,
      icon: "bx-gas-pump",
      color: "warning",
    },
    {
      title: "Remaining Fuel",
      value: `${remainingFuelUsage.toLocaleString()} L`,
      subtitle: `Avg price: ${avgPricePerLiter ? avgPricePerLiter + " BDT/L" : "-"}`,
      icon: "bx-droplet",
      color: "success",
    },
  ]

  return (
    <Row className="g-2 mb-4">
      {stats.map((stat, key) => (
        <Col md="4" key={"total_" + key}>
          <StatCard {...stat} />
        </Col>
      ))}
    </Row>
  )
}

// Period stats component with real percentage calculations
const PeriodStats = ({ summary = {}, comparison_basis = {} }) => {
  const periods = [
    {
      title: "Today",
      icon: "bx-calendar",
      data: summary.today,
      color: "warning",
      comparison: comparison_basis?.today || "Compared to yesterday",
    },
    {
      title: "This Week",
      icon: "bx-calendar-week",
      data: summary.weekly,
      color: "info",
      comparison: comparison_basis?.weekly || "Compared to previous week",
    },
    {
      title: "This Month",
      icon: "bx-calendar",
      data: summary.monthly,
      color: "primary",
      comparison: comparison_basis?.monthly || "Compared to previous month",
    },
    {
      title: "This Year",
      icon: "bx-calendar-alt",
      data: summary.yearly,
      color: "success",
      comparison: comparison_basis?.yearly || "Compared to previous year",
    },
  ]

  // Format growth percentage with tooltip
  const formatGrowth = (growthData, currentData, previousData, periodType) => {
    if (!growthData?.fuel_qty && growthData?.fuel_qty !== 0) return null

    const value = growthData.fuel_qty
    const trend = growthData.trend
    const currentQty = currentData?.total_fuel_qty || 0
    const previousQty = previousData?.total_fuel_qty || 0

    let tooltip = ""
    if (previousQty === 0) {
      tooltip = `New ${periodType.toLowerCase()} data - no previous ${periodType.toLowerCase()} records`
    } else {
      tooltip = `Growth from ${previousQty.toLocaleString()}L to ${currentQty.toLocaleString()}L compared to previous ${periodType.toLowerCase()}`
    }

    return {
      value: `${value > 0 ? "+" : ""}${value}%`,
      color:
        trend === "up" ? "success" : trend === "down" ? "danger" : "secondary",
      tooltip: tooltip,
    }
  }

  // Format spending growth with tooltip
  const formatSpendingGrowth = (
    growthData,
    currentData,
    previousData,
    periodType
  ) => {
    if (!growthData?.fuel_price && growthData?.fuel_price !== 0) return null

    const value = growthData.fuel_price
    const currentSpending = currentData?.total_fuel_price || 0
    const previousSpending = previousData?.total_fuel_price || 0

    let tooltip = ""
    if (previousSpending === 0) {
      tooltip = `New ${periodType.toLowerCase()} spending - no previous records`
    } else {
      tooltip = `Spending increased from ${previousSpending.toLocaleString()} BDT to ${currentSpending.toLocaleString()} BDT`
    }

    return {
      value: `${value > 0 ? "+" : ""}${value}%`,
      color: value >= 0 ? "success" : "danger",
      tooltip: tooltip,
    }
  }

  return (
    <Row className="g-2">
      {periods.map((period, key) => {
        const data = period.data
        const fuelQty = parseFloat(data?.total_fuel_qty ?? 0)
        const fuelPrice = parseFloat(data?.total_fuel_price ?? 0)
        const avgPrice = fuelQty > 0 ? (fuelPrice / fuelQty).toFixed(2) : "-"

        const fuelTrend = formatGrowth(
          data?.growth,
          data,
          data?.previous_period,
          period.title.replace("This ", "")
        )
        const spendingTrend = formatSpendingGrowth(
          data?.growth,
          data,
          data?.previous_period,
          period.title.replace("This ", "")
        )

        return (
          <Col md="3" key={"period_" + key}>
            <Card className="border-0 rounded-3 shadow-sm hover-lift transition-all">
              <CardBody className="p-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="text-muted mb-0 small fw-medium">
                    {period.title}
                  </h6>
                  <div
                    style={{ width: "2rem", height: "2rem" }}
                    className={`d-flex align-items-center justify-content-center rounded-circle p-1 bg-${period.color}-subtle`}
                  >
                    <i
                      className={`bx ${period.icon} text-${period.color} fs-6`}
                    ></i>
                  </div>
                </div>

                <div className="d-flex align-items-baseline justify-content-between mb-2">
                  <h4 className="mb-0 fw-bold">{fuelQty.toLocaleString()} L</h4>
                  {fuelTrend && (
                    <span
                      className={`badge bg-${fuelTrend.color}-subtle text-${fuelTrend.color} small`}
                      title={fuelTrend.tooltip}
                    >
                      {fuelTrend.value}
                    </span>
                  )}
                </div>

                <div className="space-y-1">
                  <div className="d-flex align-items-center justify-content-between small">
                    <span className="text-muted">
                      <i className="bx bx-money me-1"></i>Amount:
                      <span
                        style={{ marginLeft: "0.5rem" }}
                        className="fw-medium"
                      >
                        {fuelPrice.toLocaleString()} BDT
                      </span>
                    </span>
                    <div className="d-flex align-items-center gap-1">
                      {spendingTrend && (
                        <span
                          className={`badge bg-${spendingTrend.color}-subtle text-${spendingTrend.color} smaller`}
                          title={spendingTrend.tooltip}
                        >
                          {spendingTrend.value}
                        </span>
                      )}
                    </div>
                  </div>

                  <ValueItem
                    icon="bx-line-chart"
                    label="Avg Price"
                    value={avgPrice !== "-" ? `${avgPrice} BDT/L` : "-"}
                    tooltip={`Average price per liter for this period`}
                  />
                  <ValueItem
                    icon="bx-calendar"
                    label="Period"
                    value={`${formatDate(data?.from)} - ${formatDate(
                      data?.to
                    )}`}
                    tooltip="Date range for this period"
                  />

                  {/* Previous period comparison */}
                  {data?.previous_period && (
                    <div className="pt-1 border-top">
                      <ValueItem
                        icon="bx-history"
                        label="Previous"
                        value={`${
                          data.previous_period.total_fuel_qty?.toLocaleString() ||
                          0
                        }L`}
                        tooltip={
                          data.previous_period.total_fuel_qty > 0
                            ? `Previous ${period.title.toLowerCase()}: ${data.previous_period.total_fuel_qty.toLocaleString()}L fuel, ${
                                data.previous_period.total_fuel_price?.toLocaleString() ||
                                0
                              } BDT`
                            : `No data available for previous ${period.title.toLowerCase()}`
                        }
                      />
                      {data.previous_period.total_fuel_price > 0 && (
                        <ValueItem
                          icon="bx-money"
                          label="Prev Spending"
                          value={`${data.previous_period.total_fuel_price.toLocaleString()} BDT`}
                          className="smaller text-muted"
                          tooltip={`Total spending in previous ${period.title.toLowerCase()}`}
                        />
                      )}
                      {data.previous_period.total_fuel_qty > 0 &&
                        data.previous_period.total_fuel_price > 0 && (
                          <ValueItem
                            icon="bx-calculator"
                            label="Prev Avg Price"
                            value={`${(
                              data.previous_period.total_fuel_price /
                              data.previous_period.total_fuel_qty
                            ).toFixed(2)} BDT/L`}
                            className="smaller text-muted"
                            tooltip={`Average price per liter in previous ${period.title.toLowerCase()}`}
                          />
                        )}
                      <div className="pt-1">
                        <div
                          className="text-muted small"
                          title={period.comparison}
                        >
                          <i className="bx bx-info-circle me-1"></i>
                          {period.comparison}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        )
      })}
    </Row>
  )
}

// Main FuelPurchaseSummary component with compact layout
const FuelPurchaseSummary = ({
  totals = {},
  summary = {},
  comparison_basis = {},
}) => {
  const [show, setShow] = useState(false)

  return (
    <>
      <div className="mb-2 d-flex justify-content-end">
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() => setShow(prev => !prev)}
        >
          {show ? "Hide Summary ▲" : "Show Summary ▼"}
        </button>
      </div>
      {show && (
        <>
          <TotalStats totals={totals} />
          <PeriodStats summary={summary} comparison_basis={comparison_basis} />
        </>
      )}
    </>
  )
}

export default FuelPurchaseSummary
